<template>
  <div>
    <LoadingIndicator v-if="loading" />
    <div
      v-else-if="!!content.length"
      id="news-container"
    >
      <news
        :theme="themeSettings"
        :content="content"
        :news-config="config"
        :component-id="componentId"
        :path-prefix="pathPrefix"
        :fallback-image="fallbackImage"
        :is-preview="isThemePreview"
      />
      <DotsRectangle
        v-if="dotsRectangle.show"
        :fill-color="dotsRectangle.color"
        :flip-horizontal="dotsRectangle.flip"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import LoadingIndicator from '../../global/LoadingIndicator.vue';
import DotsRectangle from '../theme_accents/svg/DotsRectangle.vue';
import HomepageFetchHelper from '../../../helpers/homepage-fetcher.js';

defineOptions({
  name: 'ViewNews',
});

const props = defineProps({
  containerIdx: {
    type: Number,
    default: null,
  },
  componentIdx: {
    type: Number,
    default: null,
  },
  containerId: {
    type: String,
    default: null,
  },
  dotsRectangle: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();

/** Captured in onMounted so async fetch paths keep the same $axios / $staticFetch after await (getCurrentInstance() is null after await). */
const nuxtGlobs = { axios: null, staticFetch: null };

const loading = ref(true);
const content = ref([]);
const config = ref({});

const homepage = computed(() => store.state.homepage);
const theme = computed(() => store.state.theme);
const pathPrefix = computed(() => store.state.pathPrefix);
const isThemePreview = computed(() => store.state.isThemePreview);
const featureFlags = computed(() => store.state.featureFlags);
const settings = computed(() => store.state.settings);
const staticFilesConfig = computed(() => store.state.staticFilesConfig);
const mediaPreviewContent = computed(() => store.state.mediaPreviewContent);
const globals = computed(() => store.state.globals);

const getNewsApi = computed(() => store.getters.getNewsApi);
const getS3UrlForMedia = computed(() => store.getters.getS3UrlForMedia);
const getGlobalThemeSettings = computed(() => store.getters.getGlobalThemeSettings);
const getComponentTranslation = computed(() => store.getters['translation/getComponentTranslation']);

const defaultData = computed(() => mediaPreviewContent.value?.newsPreviewContent || []);

const themeSettings = computed(() => {
  if (!theme.value?.containers) return {};
  return {
    ...theme.value.containers[props.containerIdx].components[props.componentIdx].options,
    translations: getComponentTranslation.value('news'),
    globalThemeSettings: getGlobalThemeSettings.value,
  };
});

const componentId = computed(() => {
  if (isThemePreview.value) {
    return 'preview_id';
  }

  return `${props.containerId}_${props.componentIdx}`;
});

const useS3ForMedia = computed(() => featureFlags.value.cms_stored_data_news_enabled);

const fallbackImage = computed(() => {
  const newsDefaultFallbackImage = globals.value?.fallback_images?.find((item) => item.feature === 'news') || {};
  const settingsVal = settings.value || {};
  const newsFallbackImage = settingsVal.newsFallbackImage || {};
  return Object.keys(newsFallbackImage).length === 0 ? newsDefaultFallbackImage : newsFallbackImage;
});

async function fetchContentFromS3AndValidate() {
  try {
    const s3UrlForContent = getS3UrlForMedia.value('news');
    const contentResponse = await nuxtGlobs.staticFetch.get(s3UrlForContent);

    const lastS3FileUpdate = contentResponse.data?.meta?.last_static_update;
    const lastMediaUpdate =
      staticFilesConfig.value?.last_static_update?.news || staticFilesConfig.value?.homepage?.last_static_update?.news;
    const isStaticDataFresh = HomepageFetchHelper.validateLastStaticUpdate(lastS3FileUpdate, lastMediaUpdate);
    const newsArticles = contentResponse?.data?.articles;

    if (!isStaticDataFresh || !newsArticles) {
      return await fetchContentFromThrillshare();
    }

    return newsArticles;
  } catch (e) {
    console.error(e);
    return await fetchContentFromThrillshare();
  }
}

async function fetchContentFromThrillshare() {
  try {
    const { url, params } = getNewsApi.value(nuxtGlobs.axios.defaults.baseURL);
    const contentResponse = await nuxtGlobs.axios.get(url, { params, cache: { ignoreCache: false } });
    return contentResponse?.data?.articles || [];
  } catch (e) {
    console.error(e);
    return [];
  }
}

function setupThemePreview() {
  content.value = defaultData.value;
  loading.value = false;
}

async function getContent() {
  if (isThemePreview.value) {
    setupThemePreview();
    return;
  }

  if (useS3ForMedia.value) {
    content.value = await fetchContentFromS3AndValidate();
  } else {
    content.value = await fetchContentFromThrillshare();
  }
  loading.value = false;
}

onMounted(() => {
  const inst = getCurrentInstance();
  const p = inst?.proxy;
  const gp = inst?.appContext.config.globalProperties;
  nuxtGlobs.axios = p?.$axios ?? gp?.$axios;
  nuxtGlobs.staticFetch = p?.$staticFetch ?? gp?.$staticFetch;
  config.value = homepage.value.news || {};
  getContent();
});

defineExpose({
  content,
  config,
  loading,
  getContent,
  setupThemePreview,
});
</script>

<style>
#news-container {
  height: 100%;
}
</style>
